import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { APIError, FieldErrors, FieldsAPIError } from 'errors';
import StoreContext from 'contexts/storeContext';
import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import { useContext, useState } from 'react';
import { dateToBackendDateString } from 'utils';
import ToastContext from 'contexts/toastContext';
import { SalesReport } from 'types/salesReport';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export default function SalesReportDialog({ isOpen, onClose }: Props) {
  const { store } = useContext(StoreContext);
  const { api } = useContext(ApiContext);
  const { setError } = useContext(ErrorContext);
  const { setToast } = useContext(ToastContext);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(new Date(new Date().setMonth(new Date().getMonth() - 1)));
  const [allOrganizationStores, setAllOrganizationStores] = useState(false);
  const [includeOrderDetails, setIncludeOrderDetails] = useState(false);
  const [grouping, setGrouping] = useState<string>('monthly');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [loading, setLoading] = useState(false);

  const getFieldError = (field: string) => fieldErrors && fieldErrors[field];

  const addOneYear = (date: Date | null) => {
    return date ? new Date(date.getFullYear() + 1, date.getMonth(), date.getDate()) : undefined;
  };

  const onStartDateChange = (date: Date | null) => {
    if (date) {
      date.setHours(0, 0, 0, 0);
    }
    setSelectedStartDate(date);
    const maxDate = addOneYear(date);
    if (maxDate && selectedEndDate && selectedEndDate > maxDate) {
      setFieldErrors((prev) => ({
        ...prev,
        end_date: 'La fecha de fin no puede ser más de 1 año después de la fecha de inicio.',
      }));
    } else {
      setFieldErrors((prev) => {
        const { end_date, ...rest } = prev || {};
        return rest;
      });
    }
  };

  const onEndDateChange = (date: Date | null) => {
    if (date) {
      date.setHours(0, 0, 0, 0);
    }
    setSelectedEndDate(date);
    const maxDate = addOneYear(selectedStartDate);
    if (maxDate && date && date <= maxDate) {
      setFieldErrors((prev) => {
        const { end_date, ...rest } = prev || {};
        return rest;
      });
    }
  };

  const handleError = (err?: APIError) => {
    if (err) {
      if (err instanceof FieldsAPIError) setFieldErrors(err.fieldErrors);
      else setError(err);
    } else {
      setFieldErrors(undefined);
      setError(false);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    handleError();
    setLoading(true);

    const salesReport: SalesReport = {
      start_date: dateToBackendDateString(selectedStartDate || new Date()),
      end_date: dateToBackendDateString(selectedEndDate || new Date()),
      all_organization_stores: allOrganizationStores,
      include_orders: includeOrderDetails,
      grouping,
    };

    api.salesReport
      .create(salesReport, store.id)
      .then(() => {
        setToast({
          message: '¡El resumen de ventas se está generando! Llegará a tu correo en unos minutos.',
          severity: 'success',
          duration: 5000,
        });
        onClose();
      })
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={isOpen} onClose={() => onClose()}>
      <DialogTitle>Resumen de ventas</DialogTitle>
      <form noValidate onSubmit={handleSubmit}>
        <DialogContent sx={{ py: 0 }}>
          <Typography>El resumen generado será enviado a tu correo electrónico.</Typography>
          <FormControl required fullWidth>
            <DatePicker
              name='start_date'
              format='dd/MM/yyyy'
              label='Fecha de inicio'
              value={selectedStartDate}
              onChange={onStartDateChange}
              disableFuture
              sx={{ mt: 3 }}
              slotProps={{
                popper: {
                  disablePortal: false,
                  placement: 'auto',
                },
              }}
            />
            {!!getFieldError('start_date') && (
              <FormHelperText variant='filled' error={true}>
                {getFieldError('start_date')}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl required fullWidth>
            <DatePicker
              name='end_date'
              format='dd/MM/yyyy'
              label='Fecha de fin'
              value={selectedEndDate}
              onChange={onEndDateChange}
              disableFuture
              minDate={selectedStartDate || new Date()}
              maxDate={addOneYear(selectedStartDate)}
              sx={{ mt: 3 }}
              slotProps={{
                popper: {
                  disablePortal: false,
                  placement: 'auto',
                },
              }}
            />
            {!!getFieldError('end_date') && (
              <FormHelperText variant='filled' error={true}>
                {getFieldError('end_date')}
              </FormHelperText>
            )}
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <>
              <InputLabel>Agrupacion de totales</InputLabel>
              <Select
                name='grouping'
                value={grouping}
                onChange={(e) => setGrouping(e.target.value)}
                label='Agrupacion de totales'
                error={!!getFieldError('grouping')}
              >
                <MenuItem value='monthly'>Mensual</MenuItem>
                <MenuItem value='weekly'>Semanal</MenuItem>
              </Select>
            </>
            {!!getFieldError('grouping') && (
              <FormHelperText variant='filled' error={true}>
                {getFieldError('grouping')}
              </FormHelperText>
            )}
          </FormControl>
          {store.organization && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Tooltip
                title='Si seleccionas esta opción, el resumen incluirá todas las tiendas de la organización de las cuales formes parte, 
              no solo la tienda seleccionada.'
                placement='bottom'
                enterTouchDelay={0}
              >
                <MenuItem key='all_organization_stores' value='all_organization_stores'>
                  <Checkbox
                    color='primary'
                    checked={allOrganizationStores}
                    onChange={() => setAllOrganizationStores(!allOrganizationStores)}
                  />
                  <Typography
                    onClick={() => setAllOrganizationStores(!allOrganizationStores)}
                    sx={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Incluir todas las tiendas de la organización
                  </Typography>
                </MenuItem>
              </Tooltip>
              {!!getFieldError('all_organization_stores') && (
                <FormHelperText variant='filled' error={true}>
                  {getFieldError('all_organization_stores')}
                </FormHelperText>
              )}
            </FormControl>
          )}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <Tooltip
              title='Si seleccionas esta opción, el resumen incluirá detalle de todos los pedidos en el rango de tiempo.'
              placement='bottom'
              enterTouchDelay={0}
            >
              <MenuItem key='include_orders' value='include_orders'>
                <Checkbox color='primary' checked={includeOrderDetails} onChange={() => setIncludeOrderDetails(!includeOrderDetails)} />
                <Typography onClick={() => setIncludeOrderDetails(!includeOrderDetails)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
                  Incluir detalle de pedidos
                </Typography>
              </MenuItem>
            </Tooltip>
            {!!getFieldError('include_orders') && (
              <FormHelperText variant='filled' error={true}>
                {getFieldError('include_orders')}
              </FormHelperText>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onClose()} color='error'>
            Cancelar
          </Button>
          <Button disabled={loading} onClick={handleSubmit} color='primary'>
            Generar y enviar
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
